/**
 * Daily Progress Report (DPR) — web SDK port of the mobile helpers
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/dpr/dpr.ts
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/dpr/types.ts
 *
 * Schema parity is mandatory: mobile + web read and write the EXACT
 * same `dpr/{projectId}_{date}` documents. Field names and shapes
 * mirror the mobile file verbatim.
 *
 * Why doc-id uses `${projectId}_${date}`:
 *   One DPR per project per calendar day. Using a deterministic id
 *   makes the upsert idempotent (no risk of duplicate docs for the
 *   same day), and Firestore rules can check the parts of the id
 *   without needing a query.
 */
import {
  deleteDoc,
  doc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';

// ───── Types ─────────────────────────────────────────────────

/** Legacy field kept on DPR docs for back-compat; interior projects
 *  no longer edit weather in-app but the field is still rendered for
 *  legacy docs that have it set. */
export type Weather = 'clear' | 'cloudy' | 'rain' | 'hot' | 'other';

export const WEATHER_OPTIONS: {
  key: Weather;
  label: string;
  icon: string;
}[] = [
  { key: 'clear', label: 'Clear', icon: 'ph ph-sun' },
  { key: 'cloudy', label: 'Cloudy', icon: 'ph ph-cloud' },
  { key: 'rain', label: 'Rain', icon: 'ph ph-cloud-rain' },
  { key: 'hot', label: 'Hot', icon: 'ph ph-thermometer' },
  { key: 'other', label: 'Other', icon: 'ph ph-dots-three' },
];

export type DailyProgressReport = {
  id: string; // ${projectId}_${date}
  orgId: string;
  projectId: string;
  date: string; // YYYY-MM-DD
  workDone: string;
  weather: Weather;
  weatherNote: string;
  issues: string;
  tomorrowPlan: string;
  photoUris: string[];
  staffPresent: number;
  staffTotal: number;
  /** Same-day material requests (non-rejected), saved at DPR save time. */
  materialRequestedCount?: number;
  materialRequestedValue?: number;
  /** Legacy snapshots from older DPR saves (material in/out). */
  materialReceivedCount: number;
  materialUsedCount: number;
  createdBy: string;
  createdAt: { toMillis(): number } | null;
  updatedAt: { toMillis(): number } | null;
};

// ───── Doc id helper ─────────────────────────────────────────

export function dprDocId(projectId: string, date: string): string {
  return `${projectId}_${date}`;
}

// ───── Write helpers ─────────────────────────────────────────

export type UpsertDprInput = {
  orgId: string;
  projectId: string;
  date: string; // YYYY-MM-DD
  workDone: string;
  weather: Weather;
  weatherNote: string;
  issues: string;
  tomorrowPlan: string;
  photoUris: string[];
  staffPresent: number;
  staffTotal: number;
  materialRequestedCount: number;
  materialRequestedValue: number;
  materialReceivedCount: number;
  materialUsedCount: number;
  createdBy: string;
  /**
   * Whether the doc already exists. Caller passes this from the
   * `useDpr` snapshot so we skip a pre-write `.get()` (which would
   * fire the dpr-read rule against legacy docs missing `orgId` and
   * silently deny). The rule on the actual `set` is the only gate.
   */
  isUpdate: boolean;
};

/**
 * Idempotent upsert. Writes `createdAt` only on first save;
 * `updatedAt` always. `merge: true` keeps fields written by mobile
 * or future iterations untouched.
 */
export async function upsertDpr(input: UpsertDprInput): Promise<string> {
  const id = dprDocId(input.projectId, input.date);
  const ref = doc(db, 'dpr', id);
  const payload: Record<string, unknown> = {
    orgId: input.orgId,
    projectId: input.projectId,
    date: input.date,
    workDone: input.workDone,
    weather: input.weather,
    weatherNote: input.weatherNote,
    issues: input.issues,
    tomorrowPlan: input.tomorrowPlan,
    photoUris: input.photoUris,
    staffPresent: input.staffPresent,
    staffTotal: input.staffTotal,
    materialRequestedCount: input.materialRequestedCount,
    materialRequestedValue: input.materialRequestedValue,
    materialReceivedCount: input.materialReceivedCount,
    materialUsedCount: input.materialUsedCount,
    createdBy: input.createdBy,
    updatedAt: serverTimestamp(),
  };
  if (!input.isUpdate) {
    payload.createdAt = serverTimestamp();
  }
  try {
    await setDoc(ref, payload, { merge: true });
  } catch (err) {
    const code =
      err && typeof err === 'object' && 'code' in err
        ? String((err as { code: unknown }).code)
        : 'unknown';
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(
      `Save DPR failed (${id}, org=${input.orgId}, project=${input.projectId}) [${code}]: ${msg}`,
    );
  }
  return id;
}

export async function deleteDpr(id: string): Promise<void> {
  await deleteDoc(doc(db, 'dpr', id));
}

// ───── Date helpers (local-time-safe) ─────────────────────────

/** Return YYYY-MM-DD in the user's local timezone. */
export function toLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Today (local) as YYYY-MM-DD. */
export function todayLocalDateKey(): string {
  return toLocalDateKey(new Date());
}

/** Add N days (positive or negative) to a YYYY-MM-DD key. */
export function shiftDateKey(key: string, deltaDays: number): string {
  const [y, m, d] = key.split('-').map((s) => parseInt(s, 10));
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  dt.setDate(dt.getDate() + deltaDays);
  return toLocalDateKey(dt);
}

/** Human-friendly date label, e.g. "22 May 2026". */
export function formatDateKey(key: string | undefined): string {
  if (!key) return '—';
  const [y, m, d] = key.split('-').map((s) => parseInt(s, 10));
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  return dt.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Weekday label, e.g. "Fri". */
export function formatWeekdayKey(key: string | undefined): string {
  if (!key) return '';
  const [y, m, d] = key.split('-').map((s) => parseInt(s, 10));
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  return dt.toLocaleDateString('en-IN', { weekday: 'short' });
}
